import { useEffect, useState, type ReactNode } from 'react';
import { apiGet, fetchDocument, type ApiMeta, type DocMeta } from './api';
import { useI18n } from './i18n';

export interface AsyncState<T> {
  data: T | null;
  meta: ApiMeta | null;
  error: string | null;
  loading: boolean;
}

/** 统一的数据获取 Hook：返回数据、口径 meta、错误与加载态。 */
export function useApi<T>(path: string): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    meta: null,
    error: null,
    loading: true,
  });

  useEffect(() => {
    let alive = true;
    setState({ data: null, meta: null, error: null, loading: true });
    apiGet<T>(path)
      .then((r) => {
        if (alive) setState({ data: r.data, meta: r.meta, error: null, loading: false });
      })
      .catch((e: unknown) => {
        if (alive)
          setState({
            data: null,
            meta: null,
            error: e instanceof Error ? e.message : String(e),
            loading: false,
          });
      });
    return () => {
      alive = false;
    };
  }, [path]);

  return state;
}

/** 读取种子文档（apps/<site>/data/<file>）的 Hook。 */
export function useDocument<T extends { _meta: DocMeta }>(
  site: string,
  file: string,
): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    meta: null,
    error: null,
    loading: true,
  });

  useEffect(() => {
    let alive = true;
    setState({ data: null, meta: null, error: null, loading: true });
    fetchDocument<T>(site, file)
      .then((r) => {
        if (alive) setState({ data: r.data, meta: r.meta, error: null, loading: false });
      })
      .catch((e: unknown) => {
        if (alive)
          setState({
            data: null,
            meta: null,
            error: e instanceof Error ? e.message : String(e),
            loading: false,
          });
      });
    return () => {
      alive = false;
    };
  }, [site, file]);

  return state;
}

/** 统一的加载/错误呈现，避免每个页面各写一套。 */
export function StateGate<T>({
  state,
  children,
}: {
  state: AsyncState<T>;
  children: (data: T, meta: ApiMeta | null) => ReactNode;
}) {
  const { t } = useI18n();
  if (state.loading) {
    return <div className="rounded-xl border border-line bg-surface p-8 text-muted">{t('state.loading')}</div>;
  }
  if (state.error) {
    return (
      <div className="rounded-xl border border-danger/40 bg-danger/10 p-6">
        <div className="font-semibold text-danger-light">{t('error.title')}</div>
        <p className="mt-1 text-sm text-muted">{state.error}</p>
        <p className="mt-2 text-xs text-muted">{t('error.hint')}</p>
      </div>
    );
  }
  if (!state.data) return null;
  return <>{children(state.data, state.meta)}</>;
}
